import { group, topic } from "./schema.js";

export const AGENT_META = {
  role: "agent",
  title: "Agent Manual",
  kicker: "A framed desk, not a sales floor",
  whyHeading: "Why This Helps You and Your Clients",
};

export const AGENT_TREE = [
  topic({
    slug: "start-here",
    title: "Start Here",
    terms: ["overview", "desk", "getting started"],
    forYou:
      "This book is the reference beside Agent Desk. You will know most of it. The rest is here the moment a control does not click — so a live file never depends on folklore. The Guided Tour is the film. This is the book.",
    how: [
      "Sign in as an agent so the desk matches you.",
      "Start on Agent Dashboard, then Client Manager or Files depending on the day.",
      "Read the client’s checklist and notes in the File Packet before you guess.",
      "Use Client Messaging for buyers and sellers. Use Office Messaging for colleagues.",
      "If something does not click, search this book. Then continue the file.",
    ],
    why:
      "Clients describe feelings that are hard to search. Their checklists turn those instincts into specifics you can actually use — commute, lot, kitchen, access, condition, photos. A desk that holds the file in one place means fewer back-and-forth emails, fewer houses that were never going to fit, and a tone worth archiving.",
    examples:
      "If a buyer wrote that grandparents need shade, you already have a search filter. Thank them in Client Messaging. That is how a tool becomes a relationship.",
    tips: [
      "Buyer and seller files never share a carpet. Believe the badge.",
      "If you ever feel unsure, the sidebar search can guide you instantly.",
    ],
    related: ["file-packet", "client-messaging", "client-manager"],
    href: "/agent/dashboard",
  }),

  group("The desk", [
    topic({
      slug: "agent-desk",
      title: "Agent Desk",
      terms: ["dashboard", "framed desk", "morning"],
      forYou:
        "Agent Desk is a framed workshop for assigned clients — woodland, not a leaderboard.",
      how: [
        "Open Dashboard each morning.",
        "Then Clients or Files depending on who actually needs you.",
        "Fill Profile in week one so the footer on client pages looks like an office.",
      ],
      why:
        "A calm first screen is a professional courtesy to yourself. Clients feel that calm even when they never see this desk.",
      examples:
        "If the calendar shows a community event you will not attend, ignore it. Your showing is the appointment you created.",
      tips: ["Billing links may open this marketing site’s Pay page. That is not this book, and not a client thread."],
      related: ["client-manager", "calendar", "file-packet"],
      href: "/agent/dashboard",
    }),
    topic({
      slug: "client-manager",
      title: "Client Manager",
      terms: ["clients", "assigned", "people"],
      forYou:
        "Client Manager is people, not a marketplace feed — assigned buyers and sellers only.",
      how: [
        "Open the list.",
        "Open a person, then their File and Messages.",
        "If a name is missing, they are not assigned. Ask in Office Messaging, not in a client thread.",
      ],
      why:
        "You cannot serve a name you cannot find. You also cannot serve a name that is not yours. The list is a lock as much as a door.",
      examples:
        "A new buyer who finished Location but not Lifestyle still deserves a kind check-in in Client Messaging — and a pointer to their Buyer Manual if they feel stuck.",
      tips: ["Do not keep a parallel spreadsheet. The packet is the record."],
      related: ["file-packet", "client-messaging"],
      href: "/agent/clients",
    }),
  ]),

  group("The file", [
    topic({
      slug: "file-packet",
      title: "File Packet",
      terms: ["files", "packet", "profile", "checklist", "pdf"],
      forYou:
        "The File Packet is the home of the work: profile, checklist, timeline, messages, calendar, and PDF — one client, one searchable file.",
      how: [
        "Open Files, then a file.",
        "Walk the sections. Read their checklist and notes before you write yours.",
        "Add custom steps and move lifecycle when reality moves.",
        "Export once when the file actually ends. It is a snapshot, not a flyer.",
      ],
      why:
        "Sticky notes and paper planners are what this desk replaces. A packet means you leave with something usable, and so does the client.",
      examples:
        "Before a listing appointment, open the seller packet, skim Condition notes, then Client Messaging. That order is a habit, not a scramble.",
      tips: ["Buyer packets and seller packets stay labeled. Never rewrite buyer pages to “share” with seller."],
      related: ["custom-steps", "lifecycle", "reports"],
      href: "/agent/files",
    }),
    topic({
      slug: "custom-steps",
      title: "Custom steps",
      terms: ["office steps", "lockbox", "beats"],
      forYou:
        "Custom steps hold office-specific beats on the packet — lockbox, signs, photographer — without rewriting the client journey.",
      how: [
        "Open the file.",
        "Add a step, status it, attach if needed.",
        "Write as if the archive will be read later.",
      ],
      why:
        "Every office has a ritual. Rituals that live only in a binder get skipped. Rituals on the packet get done.",
      examples:
        "“Order lockbox” is a custom step. “Buyer Location page” is not — that is their checklist.",
      tips: ["Vendor names belong in the note. This desk is not a marketplace."],
      related: ["file-packet", "lifecycle", "documents-photos"],
    }),
    topic({
      slug: "lifecycle",
      title: "Lifecycle tracker",
      terms: ["status", "pipeline", "under contract"],
      forYou:
        "Lifecycle shows where a file sits — honestly, not as a race.",
      how: [
        "Open the file.",
        "Move status when reality moves.",
        "Close or abandon when the work is done, not when you are frustrated.",
      ],
      why:
        "A pipeline game makes people rush. A tracker makes people honest. Clients feel the difference even if they never see this control.",
      examples:
        "Under contract is a status, not a celebration post. Note it. Message the client kindly.",
      tips: ["Lifecycle is status. Timeline is story. Reports snapshot both."],
      related: ["file-packet", "reports"],
    }),
    topic({
      slug: "reports",
      title: "Reports",
      terms: ["pdf", "export", "communication report", "archive"],
      forYou:
        "Reports are snapshots for the archive — header, timeline, messages, attachments, checklist summary — not neighborhood flyers.",
      how: [
        "Export from close or abandon when the file actually ends.",
        "Keep the tone you would show a broker.",
        "Client email of the report is opt-in in this prototype. You and the broker are flagged.",
      ],
      why:
        "Memory is a poor archive. A kind last message and a clean export beat a frantic Friday PDF.",
      examples:
        "A closed buyer file with messages and a checklist summary is the report. A glossy postcard is not.",
      tips: ["Export once. The snapshot is the archive."],
      related: ["file-packet", "lifecycle", "client-messaging"],
    }),
    topic({
      slug: "documents-photos",
      title: "Documents & photos",
      terms: ["vault", "uploads", "media"],
      forYou:
        "One account vault for checklist uploads, message files, and packet files — not a public gallery.",
      how: [
        "Open Documents & Photos.",
        "Filter by client.",
        "Respect size limits: documents under 2 MB; photos compress locally.",
      ],
      why:
        "Scattering media in email is how packets go hollow. What you can see, you can use in a showing conversation.",
      examples:
        "A seller’s kitchen set should appear here and on their journey — not only in a personal camera roll.",
      tips: ["Words first. Files when words are not enough. Teach clients the same limits; their manuals say it too."],
      related: ["file-packet", "custom-steps"],
      href: "/agent/documents",
    }),
  ]),

  group("Staying in touch", [
    topic({
      slug: "client-messaging",
      title: "Client Messaging",
      terms: ["buyer thread", "seller thread", "rapport"],
      forYou:
        "Client Messaging is the quiet thread with the file. Office chat is the other room.",
      how: [
        "Open Client Messaging.",
        "Pick the thread. Write as archive-worthy.",
        "Answer the human question. If it is a product control, send their Buyer or Seller Manual.",
      ],
      why:
        "Rapport is built in complete sentences. Clients are told in their books to ask. When they do, the thread is doing its job.",
      examples:
        "Earnest-money fog: explain here, and point at their Transaction topic. Do not paste a legal treatise. Thank them for a porch note — that is teaching, not extra work.",
      tips: ["Unread sits on this nav item, not on Office. Never the place for colleague venting."],
      related: ["office-messaging", "file-packet"],
      href: "/agent/messages",
    }),
    topic({
      slug: "office-messaging",
      title: "Office Messaging",
      terms: ["colleagues", "huddle", "inquiry"],
      forYou:
        "Office Messaging is colleagues and inquiries — culture when the client is not in the room.",
      how: [
        "Open Office Messaging.",
        "Keep it worthy of a broker’s later read.",
        "Start with Inquiry on the office desk when that is the work.",
      ],
      why:
        "A leaked office joke into a client file is the disaster this furniture is built to avoid. Two rooms, one store, never mixed on screen.",
      examples:
        "Lockbox vendor timing is office. The client’s shade note is client.",
      tips: ["When in doubt, ask: would I want this in the communication report?"],
      related: ["client-messaging", "custom-steps"],
      href: "/agent/office",
    }),
    topic({
      slug: "calendar",
      title: "Calendar",
      terms: ["showings", "appointments", "hub events"],
      forYou:
        "Desk appointments you create; hub community events that appear when relevant as read-only rows.",
      how: [
        "Create showings and file dates on Agent Calendar.",
        "Request times from clients in Client Messaging, then record them here.",
        "Leave hub rows alone. They will not save back into your appointment list.",
      ],
      why:
        "Dates that live only in texts get lost. Dates on the desk live with the file. Tours that fit someone’s pickup schedule are how you earn trust.",
      examples:
        "Inspection Tuesday 9:00 is a desk event. A county housing forum is hub. Both can appear. Only one is yours to edit.",
      tips: ["Region default on hub events is San Diego County. Southern California is next."],
      related: ["file-packet", "client-messaging"],
      href: "/agent/calendar",
    }),
  ]),
];
